// @flow

import {extend} from '../../util/util';
import {FilterControlBase} from './filter_control_base';
import {Common} from '../../util/enums';
import type {Options} from './filter_control_base';
import type {VehicleTypeEnum, TruckUnitEnum} from '../../util/enums';

type VehicleOptions =  {
    vehicleType?: VehicleTypeEnum;
    trkUnits?: TruckUnitEnum;
    trkHeight?: string;
    trkLength?: string;
    trkWidth?: string;
    trkWeight?: string;
    trkAxles?: number;
    trkLCV?: boolean;
}

type TruckRestrictionOptions =  Options & {
    category: ?string,
    vehicleOptions?: VehicleOptions
};

const defaultOptions: TruckRestrictionOptions = {
    id: 'truckrestrictions',
    loadingEventId: 'truckrestriction',
    isVisible: true,
    category: 'All'
};
/**
 * The `TruckRestrictionFilterControl` adds the ability to filter the truck restrictions.
 *
 * @implements {IControl}
 * @param {Object} options
 * @param {string} [options.category='All'] Filters results based on the category ("All", "general", dimension", "hazmat", "multiple"). Default is 'All'.
 * @param {boolean} [options.isVisible=true] Controls the visiblity of the selection controls.
 * @param {Object} [options.vehicleOptions] Vehicle options used to filter the visibility of restrictions. This helps to only display restrictions that are relevant to the vehicle.
 *
 * @example
 * var truckRestrictionFilterControl = TrimbleMaps.TruckRestrictionFilterControl();
 * map.addControl(truckRestrictionFilterControl);
 */
class TruckRestrictionFilterControl extends FilterControlBase {
    _vehicleOptions: VehicleOptions;
    constructor(options: TruckRestrictionOptions) {
        const opt = extend({}, defaultOptions, options);
        super(opt);
        const values = ['All', 'general', 'dimensions', 'hazmat'];
        let defaultFilter = values[0];
        if (values.indexOf(opt.category) > -1) {
            defaultFilter = opt.category;
        }
        this._layers = ['truckrestrictions'];
        this._filterTitle = 'Truck Restriction Filter';
        this._filterObjects = [{
            id: 'category',
            label: 'Category',
            texts: ['All', 'General', 'Dimension', 'Hazmat'],
            values,
            default: defaultFilter
        }];
        this._vehicleOptions = opt.vehicleOptions;
    }

    _getFilter() {
        const filter: any[] = super._getFilter();

        // if no vehicle options are set, return the base filter.
        if (typeof this._vehicleOptions !== 'object') return filter;

        const vehicleFilter = this._buildVehicleFilter();

        // only add if there are new filters
        if (vehicleFilter.length > 1) {
            filter.push(vehicleFilter);
        }

        return filter;
    }

    _buildVehicleFilter() {
        const vehicleFilter = ['any'];

        if (this._vehicleOptions.vehicleType !== undefined) {
            if (this._vehicleOptions.vehicleType === Common.VehicleType.TRUCK) {
                vehicleFilter.push([
                    'any',
                    ['!has', 'vehType'],
                    ['has', 'IsTruckRestricted'],
                    ['in', 'vehType', 'Truck,Auto,LightTruck'],
                ]);
            }
        }

        if (this._vehicleOptions.trkHeight !== undefined) {
            vehicleFilter.push([
                'any',
                ['!has', 'height'],
                [
                    'all',
                    ['>', 'height', 0],
                    ['<=', 'height', this._vehicleOptions.trkHeight]
                ],
            ]);
        }

        if (this._vehicleOptions.trkLength !== undefined) {
            vehicleFilter.push([
                'any',
                ['!has', 'length'],
                [
                    'all',
                    ['>', 'length', 0],
                    ['<=', 'length', this._vehicleOptions.trkLength]
                ],
            ]);
        }

        if (this._vehicleOptions.trkWidth !== undefined) {
            vehicleFilter.push([
                'any',
                ['!has', 'width'],
                [
                    'all',
                    ['>', 'width', 0],
                    ['<=', 'width', this._vehicleOptions.trkWidth]
                ],
            ]);
        }

        if (this._vehicleOptions.trkWeight !== undefined) {
            vehicleFilter.push([
                'any',
                ['!has', 'weight'],
                [
                    'all',
                    ['>', 'weight', 0],
                    ['<=', 'weight', this._vehicleOptions.trkWeight]
                ],
            ]);
        }

        if (this._vehicleOptions.trkAxles !== undefined) {
            vehicleFilter.push([
                'any',
                ['!has', 'axleLoad'],
                [
                    'all',
                    ['>', 'axleLoad', 0],
                    ['<=', 'axleLoad', this._vehicleOptions.trkAxles]
                ],
            ]);
        }

        if (this._vehicleOptions.trkLCV !== undefined) {
            vehicleFilter.push([
                'any',
                ['!has', 'trailer'],
                [
                    'all',
                    ['==', 'trailer', this._vehicleOptions.trkLCV]
                ],
            ]);
        }

        return vehicleFilter;
    }
}

export default TruckRestrictionFilterControl;
